import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Archive, Undo2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getBoardMembers, createBoardMember, archiveBoardMember, unarchiveBoardMember } from '@/lib/api';

export function TeamMembersDialog({ open, onOpenChange, onRefresh }) {
  const [members, setMembers] = useState([]);
  const [archived, setArchived] = useState([]);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const fetchMembers = async () => {
    try {
      const all = await getBoardMembers({ include_archived: true });
      setMembers(all.filter(m => !m.is_archived));
      setArchived(all.filter(m => m.is_archived));
    } catch { toast.error('Failed to load members'); }
  };

  useEffect(() => { if (open) fetchMembers(); }, [open]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await createBoardMember({ name: newName.trim() });
      setNewName('');
      toast.success('Team member added');
      fetchMembers();
      onRefresh?.();
    } catch { toast.error('Failed to add'); }
    finally { setAdding(false); }
  };

  const handleArchive = async (id) => {
    try {
      await archiveBoardMember(id);
      toast.success('Member archived');
      fetchMembers();
      onRefresh?.();
    } catch { toast.error('Failed to archive'); }
  };

  const handleUnarchive = async (id) => {
    try {
      await unarchiveBoardMember(id);
      toast.success('Member restored');
      fetchMembers();
      onRefresh?.();
    } catch { toast.error('Failed to restore'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="team-members-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-semibold">Team Members</DialogTitle>
        </DialogHeader>

        {/* Add new */}
        <div className="flex gap-2">
          <Input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="New team member name..."
            className="h-9 text-[13px] flex-1"
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
            data-testid="new-member-name"
          />
          <Button size="sm" className="h-9 bg-teal-600 hover:bg-teal-700" onClick={handleAdd} disabled={adding || !newName.trim()} data-testid="add-member-btn">
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Current members */}
        <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
          {members.length === 0 ? (
            <p className="text-[13px] text-gray-400 text-center py-4">No team members</p>
          ) : members.map(m => (
            <div key={m.id} className="flex items-center justify-between px-3 py-2 rounded-lg border hover:bg-gray-50" data-testid={`member-${m.id}`}>
              <div className="flex items-center gap-2">
                <span className="h-7 w-7 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-[12px] font-bold">
                  {m.name.charAt(0).toUpperCase()}
                </span>
                <span className="text-[13px] font-medium text-gray-800">{m.name}</span>
              </div>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-gray-400 hover:text-amber-600" onClick={() => handleArchive(m.id)} data-testid={`archive-member-${m.id}`}>
                <Archive className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>

        {/* Archived */}
        {archived.length > 0 && (
          <div className="border-t pt-3">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="text-[12px] text-gray-500 hover:text-gray-700 font-medium"
            >
              Archived ({archived.length}) {showArchived ? '▴' : '▾'}
            </button>
            {showArchived && (
              <div className="space-y-1.5 mt-2">
                {archived.map(m => (
                  <div key={m.id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-dashed opacity-60 hover:opacity-100">
                    <span className="text-[13px] text-gray-600">{m.name}</span>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-gray-400 hover:text-teal-600" onClick={() => handleUnarchive(m.id)} data-testid={`unarchive-member-${m.id}`}>
                      <Undo2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
